/**
 * notificationConverters.ts
 * -----------------------------------------------------------------------------
 * Converts notification data to NodeData format for orbital visualization.
 * Unread URGENT notifications appear as nodes on the notification orbit (radius 230).
 */

import { NodeData } from '../../molecules/OrbitalVisualization/constants';
import { NotificationRecord } from '../../organisms/NotificationFeed/useNotificationFeed';
import alertUnreadUrgentIcon from '../../../assets/images/alert-unread-urgent.png';

/**
 * Get notification icon - using the urgent alert icon
 */
function getNotificationIcon(): string {
  return alertUnreadUrgentIcon;
}

/**
 * Map notification priority to gravity score for orbit assignment.
 * Orbit 4 (radius 230) has gravityRange { min: 40, max: 59 }
 *
 * Priority mapping:
 * - urgent: 55-59 (top of range, most prominent)
 * - high: 50-54
 * - normal: 45-49
 * - low: 40-44
 */
function getGravityFromPriority(priority?: string): number {
  switch (priority) {
    case 'urgent':
      return 57; // High visibility on notification orbit
    case 'high':
      return 52;
    case 'normal':
      return 47;
    case 'low':
      return 42;
    default:
      return 47; // Default to normal
  }
}


/**
 * Convert a NotificationRecord to NodeData for orbital visualization.
 * Only URGENT notifications appear on orbit 4 (radius 230, gravityRange: 40-59).
 *
 * @param notification - Notification data (must be urgent)
 * @returns NodeData for orbital display
 */
export function convertNotificationToNodeData(notification: NotificationRecord): NodeData {
  const priority = notification.priority || 'normal';
  const gravity = getGravityFromPriority(priority);

  // Create label from notification type (first 2 chars)
  const typeLabel = notification.type.substring(0, 2).toUpperCase();

  // Use the urgent alert icon
  const notificationIcon = getNotificationIcon();

  const nodeData: any = {
    id: `notification-${notification.id}`,
    label: typeLabel,
    customLabel: notification.title,
    imagePath: notificationIcon, // Use urgent alert PNG icon
    labelBackground: true, // Always show background for urgent notifications
    velocity: 0, // Notifications don't have velocity
    gravity,
    urgency: 5, // Always urgent
    impact: 80, // High impact
    // DON'T set sphereData - notification nodes should use simple image rendering
    // Store notification data in a custom property instead
    notificationData: notification,
  };

  return nodeData as NodeData;
}

/**
 * Convert an array of notifications to NodeData array.
 *
 * @param notifications - Array of unread notifications
 * @returns Array of NodeData for orbital visualization
 */
export function convertNotificationsToNodeData(notifications: NotificationRecord[]): NodeData[] {
  return notifications.map(convertNotificationToNodeData);
}

/**
 * Filter and sort notifications for orbital display.
 * - Only URGENT unread notifications
 * - Sorted by created_at (newest first)
 * - Limited to top N (to avoid overcrowding)
 *
 * @param notifications - All notifications
 * @param maxCount - Maximum number to display (default: 10)
 * @returns Filtered and sorted urgent notifications
 */
export function filterNotificationsForOrbit(
  notifications: NotificationRecord[],
  maxCount = 10
): NotificationRecord[] {
  return notifications
    .filter(n => n.status !== 'read' && n.priority === 'urgent') // Only unread URGENT
    .sort((a, b) => {
      // Sort by created_at (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    })
    .slice(0, maxCount); // Limit to maxCount
}
